/**
 * Колокольчик уведомлений платформы (миграция 0259).
 *
 * Отдельно от «Что нового»: там анонсы изменений в системе, здесь — рабочие
 * события, адресованные лично («подписан договор, с вас оплата в 3 дня»).
 * Смешивать нельзя: анонс можно пролистать, поручение по договору — нет.
 *
 * Лента не зависит от прав на агентов. Бухгалтер и механик в раздел «Юрист»
 * не ходят, и до этого колокольчика узнать о подписанном договоре им было
 * неоткуда. Поэтому «Принял в работу» отмечается прямо здесь, а в карточку
 * договора ведём только тех, у кого «Юрист» открыт.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IconBellAlert } from '@shared/ui/icons';
import { Button } from '@shared/ui/Button';
import {
  useAckContractHandoffMutation,
  useListNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { fieldNotificationLink } from '@entities/field';
import type { PlatformNotification } from '@entities/notification/types';

const Wrap = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const Count = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.status.warning};
  color: #000;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: min(420px, calc(100vw - 32px));
  max-height: min(70vh, 560px);
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  z-index: ${({ theme }) => theme.z.overlay};
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
`;

const PanelTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
`;

const LinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.text.muted};
  font: inherit;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Item = styled.div<{ $unread: boolean }>`
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ $unread, theme }) => ($unread ? theme.colors.surface : 'transparent')};

  & + & {
    margin-top: ${({ theme }) => theme.spacing(1)};
  }
`;

/* Кнопка «Принял в работу» не может лежать внутри кнопки-уведомления, поэтому
   кликабельна только эта часть, а действия идут строкой ниже. */
const ItemMain = styled.button<{ $link: boolean }>`
  display: block;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  text-align: left;
  cursor: ${({ $link }) => ($link ? 'pointer' : 'default')};

  &:hover {
    background: ${({ $link, theme }) => ($link ? theme.colors.surfaceHover : 'transparent')};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`;

const ItemActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `0 ${theme.spacing(2.5)} ${theme.spacing(2)}`};
`;

const Accepted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.success};
`;

const AckError = styled.div`
  flex-basis: 100%;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const ItemTitle = styled.div<{ $unread: boolean }>`
  font-size: 13px;
  font-weight: ${({ $unread }) => ($unread ? 700 : 500)};
  margin-bottom: 2px;
`;

const ItemBody = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  /* Тело приходит с переносами: перечень поручений построчно. */
  white-space: pre-line;
`;

const ItemTime = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  text-align: center;
`;

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Куда ведёт уведомление: путь и подсказка к переходу. */
interface NotificationLink {
  to: string;
  title: string;
}

/** Какие разделы пользователю открыты — переход только туда, где нет запрета. */
interface LinkAccess {
  contracts: boolean;
  field: boolean;
}

/** Куда ведёт уведомление. Поручение Руководителя исполнителю — в «Мои
 *  поручения» (доступны всем), выдавшему — в реестр поручений. Рапорт и факт
 *  объёма Прораба («Рапорт дополнен после сдачи», «Прораб не согласен с правкой
 *  объёма») — в их карточки, если открыт агент Прораба. Заявка между функциями
 *  (новая заявка исполнителям, ответ или возврат инициатору) — в карточку
 *  заявки: её видят и автор, и исполнители, права проверяет сервер. Заявка на
 *  оплату (новая — Финансисту, решение и напоминание о закрывающих — автору) —
 *  в её карточку, она открыта без прав на агента. Карточка договора живёт в
 *  «Юристе»: без доступа к нему переход упрётся в запрет. */
function linkTo(n: PlatformNotification, can: LinkAccess): NotificationLink | null {
  if (n.ref_type === 'meeting' && n.ref_id) {
    return { to: `/agents/meetings/meetings/${n.ref_id}`, title: 'Открыть совещание' };
  }
  if (n.ref_type === 'meeting_assignment') {
    return {
      to: n.ref_id ? `/agents/meetings/register?assignment=${n.ref_id}` : '/agents/meetings/register',
      title: 'Открыть реестр поручений',
    };
  }
  if (n.ref_type === 'progress_instruction' && n.ref_id) {
    return { to: `/instructions?id=${n.ref_id}`, title: 'Открыть поручение' };
  }
  if (n.ref_type === 'function_request' && n.ref_id) {
    return { to: `/requests/${n.ref_id}`, title: 'Открыть заявку' };
  }
  if (n.ref_type === 'pay_request' && n.ref_id) {
    return { to: `/pay-requests/${n.ref_id}`, title: 'Открыть заявку на оплату' };
  }
  if (n.ref_type === 'progress_instruction_issued') {
    return {
      to: n.ref_id ? `/agents/progress/instructions?id=${n.ref_id}` : '/agents/progress/instructions',
      title: 'Открыть поручения',
    };
  }
  if (n.ref_type === 'ks_package') {
    return {
      to: n.ref_id ? `/agents/smeta/ks-packages?package=${n.ref_id}` : '/agents/smeta/ks-packages',
      title: 'Открыть пакет КС-3',
    };
  }
  if (n.ref_type === 'ksp_alert') {
    return {
      to: n.ref_id ? `/agents/ksp?alert=${n.ref_id}` : '/agents/ksp',
      title: 'Открыть предупреждение КСП',
    };
  }
  if (n.ref_type === 'geodesy_scheme') {
    return {
      to: n.ref_id ? `/agents/geodesy/schemes/${n.ref_id}` : '/agents/geodesy',
      title: 'Открыть исполнительную схему',
    };
  }
  if (n.ref_type === 'backup_health') {
    // Сбой резервного копирования базы (Б-1) — администраторам, в блок метрик.
    return { to: '/agents/statut/metrics', title: 'Открыть метрики резервного копирования' };
  }
  if (n.ref_type === 'fsnb_edition') {
    // Редакция ФСНБ разобрана / не загрузилась / введена в действие.
    return {
      to: n.ref_id
        ? `/agents/smeta/norm-base?tab=editions&edition=${n.ref_id}`
        : '/agents/smeta/norm-base?tab=editions',
      title: 'Открыть редакцию',
    };
  }
  if (n.ref_type === 'fsnb_registry') {
    // На ФГИС ЦС вышло дополнение ФСНБ — туда, где его загружают.
    return { to: '/agents/smeta/norm-base?tab=editions', title: 'Открыть нормативную базу' };
  }
  const field = fieldNotificationLink(n.ref_type, n.ref_id);
  if (field) return can.field ? field : null;
  const contractId = n.handoff_contract_id ?? (n.ref_type === 'contract' ? n.ref_id : null);
  if (!contractId) return null;
  return can.contracts
    ? { to: `/agents/statut/contracts/${contractId}`, title: 'Открыть договор' }
    : { to: `/contracts/${contractId}/brief`, title: 'Открыть паспорт договора' };
}

function errorText(err: unknown): string {
  const e = err as { status?: number; data?: { error?: { message?: string } } };
  if (e?.status === 403) return 'Отметить может только участник службы, которой передан договор.';
  const msg = e?.data?.error?.message;
  return msg && msg.trim() ? msg : 'Не удалось отметить. Попробуйте ещё раз.';
}

const AckInput = styled.input`
  flex: 1 1 180px;
  min-width: 140px;
  padding: 5px 8px;
  font-size: 12px;
  font-family: inherit;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const AckNoteText = styled.div`
  flex-basis: 100%;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Опрос раз в минуту: уведомление о договоре не срочное до секунды, а
  // лишний WS-канал ради него заводить не стоит.
  const { data: items = [] } = useListNotificationsQuery(undefined, { pollingInterval: 60000 });
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();
  const [ack, ackState] = useAckContractHandoffMutation();
  const [ackError, setAckError] = useState<{ id: string; text: string } | null>(null);
  const [ackNotes, setAckNotes] = useState<Record<string, string>>({});
  const { can_read: canOpenContracts } = useAgentPermission('statut');
  // Рапорты и факты Прораба открываются только с доступом к агенту Прораба.
  const { can_read: canOpenField } = useAgentPermission('field');
  const access: LinkAccess = { contracts: canOpenContracts, field: canOpenField };

  const unread = items.reduce((n, it) => (it.read_at == null ? n + 1 : n), 0);

  // Клик мимо панели закрывает её — иначе она висит поверх страницы.
  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  async function onItemClick(n: PlatformNotification) {
    if (n.read_at == null) {
      try {
        await markRead(n.id).unwrap();
      } catch {
        // Не критично: не отметилось — останется непрочитанным.
      }
    }
    const link = linkTo(n, access);
    if (link) {
      setOpen(false);
      navigate(link.to);
    }
  }

  async function onAck(n: PlatformNotification) {
    if (!n.handoff_id) return;
    setAckError(null);
    const note = ackNotes[n.id]?.trim();
    try {
      await ack({
        id: n.handoff_id,
        contractId: n.handoff_contract_id ?? '',
        note: note || undefined,
      }).unwrap();
      // Принял в работу — значит, и прочитал.
      if (n.read_at == null) await markRead(n.id).unwrap();
    } catch (err) {
      setAckError({ id: n.id, text: errorText(err) });
    }
  }

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={unread > 0 ? `Уведомления, непрочитанных: ${unread}` : 'Уведомления'}
        title="Уведомления"
      >
        <IconBellAlert size={18} />
        {unread > 0 && <Count aria-hidden="true">{unread > 99 ? '99+' : unread}</Count>}
      </Trigger>

      {open && (
        <Panel role="dialog" aria-label="Уведомления">
          <PanelHead>
            <PanelTitle>Уведомления</PanelTitle>
            {unread > 0 && (
              <LinkButton type="button" onClick={() => markAllRead()}>
                Отметить всё прочитанным
              </LinkButton>
            )}
          </PanelHead>

          {items.length === 0 ? (
            <Empty>Пока ничего нет.</Empty>
          ) : (
            items.map((n) => {
              const link = linkTo(n, access);
              return (
                <Item key={n.id} $unread={n.read_at == null}>
                  <ItemMain
                    type="button"
                    $link={!!link || n.read_at == null}
                    onClick={() => onItemClick(n)}
                    title={link?.title}
                  >
                    <ItemTitle $unread={n.read_at == null}>{n.title}</ItemTitle>
                    <ItemBody>{n.body}</ItemBody>
                    <ItemTime>{when(n.created_at)}</ItemTime>
                  </ItemMain>
                  {n.handoff_id && (
                    <ItemActions>
                      {n.handoff_acknowledged_at ? (
                        <>
                          <Accepted>
                            ✓ Принято в работу
                            {n.handoff_acknowledged_by_name
                              ? ` · ${n.handoff_acknowledged_by_name}`
                              : ''}{' '}
                            · {when(n.handoff_acknowledged_at)}
                          </Accepted>
                          {n.handoff_ack_note && (
                            <AckNoteText>Комментарий: {n.handoff_ack_note}</AckNoteText>
                          )}
                        </>
                      ) : (
                        <>
                          <AckInput
                            type="text"
                            value={ackNotes[n.id] ?? ''}
                            onChange={(e) =>
                              setAckNotes((prev) => ({ ...prev, [n.id]: e.target.value }))
                            }
                            placeholder="Комментарий при приёмке (опц.)"
                          />
                          <Button
                            $size="sm"
                            onClick={() => onAck(n)}
                            disabled={ackState.isLoading}
                            title="Юрист увидит в карточке договора, что ваша служба приняла его"
                          >
                            Принял в работу
                          </Button>
                        </>
                      )}
                      {ackError?.id === n.id && <AckError role="alert">{ackError.text}</AckError>}
                    </ItemActions>
                  )}
                </Item>
              );
            })
          )}
        </Panel>
      )}
    </Wrap>
  );
}
